import { LinearProgress, Tooltip } from '@mui/material'
import { NextPage } from 'next'
import React, { useContext } from 'react'
import ProfileContext from '@/components/Context/ProfileContext'
import FrancoInfos from '@/components/Franco/FrancoInfos'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import dynamic from 'next/dynamic'

// Charge react-confetti-explosion en lazy : effet visuel rare, hors first-load JS.
const ReactConfetti = dynamic(() => import('@/components/Common/Header/Confetti'), { ssr: false })

type Props = {
  amountFranco?: number
  totalPrice?: number
  group?: any
  shippingSelected?: any
  libelle?: string
}

export const getAmountMaxFranco = (amountFranco, profile) => {
  return Math.min(amountFranco, Number(profile.globalFrancoMagic))
}

type FrancoParamsReturn = {
  francoMagiq: boolean
  isGlobal: boolean
  message: string
  tooltip?: string
  percentProgress: number
  amountFranco: number
  amountMaxFranco: number
  hideFranco?: boolean
}

const getFrancoParams = (
  basket,
  profile,
  amountFranco,
  totalPrice,
  group,
  shippingSelected
): FrancoParamsReturn => {
  const francoMagiq = basket.subtotal_franco_operator >= profile.globalFrancoMagic
  const isGlobal = !(group && shippingSelected)

  const eligibleFranco = basket.eligible_for_franco

  const amountMaxFranco = getAmountMaxFranco(amountFranco, profile)

  if (isGlobal) {
    return !francoMagiq
      ? {
          francoMagiq: francoMagiq,
          isGlobal: isGlobal,
          message: eligibleFranco
            ? n(profile.globalFrancoMagic - basket.subtotal_franco_operator)
            : 'Non éligible car panier LAP dégroupé',
          percentProgress:
            100 -
            ((profile.globalFrancoMagic - basket.subtotal_franco_operator) /
              profile.globalFrancoMagic) *
              100,
          amountFranco: profile.globalFrancoMagic - basket.subtotal_franco_operator,
          amountMaxFranco,
        }
      : {
          francoMagiq,
          isGlobal,
          message: `Livraison offerte`,
          amountFranco: 0,
          percentProgress: 100,
          amountMaxFranco,
        }
  } else {
    const shipping = group.shippings.find(s =>
      basket.chosen_shipping.find(c => c === s.shipping_id)
    )

    const hasShippingCost = shipping?.rate > 0
    const hideFranco =
      (!amountMaxFranco && !francoMagiq && hasShippingCost) || profile?.groupAeliaStore

    return hideFranco
      ? {
          francoMagiq,
          isGlobal,
          message: '',
          percentProgress: 0,
          amountFranco: 0,
          amountMaxFranco,
          hideFranco,
        }
      : francoMagiq || totalPrice > amountMaxFranco || shipping?.rate == 0
        ? {
            francoMagiq,
            isGlobal,
            message: 'Livraison offerte',
            tooltip: 'Cette livraison est offerte',
            percentProgress: 100,
            amountFranco: 0,
            amountMaxFranco,
            hideFranco,
          }
        : {
            francoMagiq,
            isGlobal: isGlobal,
            message: n(amountMaxFranco - totalPrice),
            tooltip: `Plus que ${n(amountMaxFranco - totalPrice)} pour avoir la livraison gratuite`,
            percentProgress: 100 - ((amountMaxFranco - totalPrice) / amountMaxFranco) * 100,
            amountFranco: amountMaxFranco - totalPrice,
            amountMaxFranco,
            hideFranco,
          }
  }
}

/**
 * Seperation du composant en 4 types :
 * T1 : Global + FranqoMagiq atteint
 * T2 : Global + FrancoMagiq NON atteint
 * T3 : Basket + Franco ou FrancoMagiq atteint
 * T4 : Basket + Franco && FrancoMagiq NON atteint
 *
 *
 * @param param0
 * @returns
 */

const FrancoProgress: NextPage<Props> = ({
  amountFranco = null,
  totalPrice = null,
  group = null,
  shippingSelected = null,
}) => {
  const { profile, basket, basketLoading } = useContext(ProfileContext)
  if (basketLoading || !basket || !!profile?.groupAeliaStore || !profile?.globalFrancoMagic) return null

  const {
    francoMagiq,
    isGlobal,
    message,
    percentProgress,
    amountFranco: _amountFranco,
    hideFranco,
    tooltip,
  } = getFrancoParams(basket, profile, amountFranco, totalPrice, group, shippingSelected)

  if (hideFranco) return null

  return (
    <>
      {isGlobal && <ReactConfetti francoMagiq={francoMagiq} />}
      <Tooltip title={tooltip || <FrancoInfos />}>
        <div className="w-16 sm:w-24 cursor-help">
          <div className="text-[10px] sm:text-xs w-full flex items-center justify-center text-orange-400">
            {_amountFranco > 0 ? (
              <div className="flex items-center gap-0.5 sm:gap-2">
                <LocalShippingIcon sx={{ fontSize: { xs: 14, sm: 20 } }} /> {message}
              </div>
            ) : (
              'Livraison offerte'
            )}
          </div>

          <LinearProgress value={percentProgress} variant="determinate" />
        </div>
      </Tooltip>
    </>
  )
}

export default FrancoProgress
